/**
 * useRouteHelp — contextual help for the current (or provided) route.
 *
 * Pulls support/help/FAQ entities linked via knowledge-graph `documented-by`
 * relationships through `knowledgeHelpForRoute()`. Results are cached per
 * normalized route path for the lifetime of the page session.
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { knowledgeHelpForRoute } from '@/lib/knowledge/knowledge-api'
import type { KnowledgeEntity } from '@/types/knowledge'

/** Session-lifetime cache keyed by normalized pathname. */
const helpCache = new Map<string, KnowledgeEntity[]>()
/** Deduplicate concurrent fetches for the same route. */
const inflight = new Map<string, Promise<KnowledgeEntity[]>>()

function normalizeRoutePath(path: string): string {
  if (!path) return '/'
  const withSlash = path.startsWith('/') ? path : `/${path}`
  if (withSlash.length > 1 && withSlash.endsWith('/')) {
    return withSlash.slice(0, -1)
  }
  return withSlash
}

async function fetchHelpForRoute(routePath: string): Promise<KnowledgeEntity[]> {
  const cached = helpCache.get(routePath)
  if (cached) return cached

  const existing = inflight.get(routePath)
  if (existing) return existing

  const request = knowledgeHelpForRoute(routePath)
    .then((entities) => {
      const list = Array.isArray(entities) ? entities : []
      helpCache.set(routePath, list)
      return list
    })
    .finally(() => {
      inflight.delete(routePath)
    })

  inflight.set(routePath, request)
  return request
}

export interface UseRouteHelpResult {
  /** Help entities linked to this route (empty when none / not ready). */
  articles: KnowledgeEntity[]
  /** True when at least one article is available. */
  hasHelp: boolean
  /** True while the first fetch for this route is in flight. */
  loading: boolean
  /** Normalized route path used for the lookup. */
  routePath: string
  /** Fetch error message, if any (button stays hidden on error). */
  error: string | null
}

/**
 * Load contextual help for `routePath`, or the current location pathname
 * when omitted.
 */
export function useRouteHelp(routePath?: string): UseRouteHelpResult {
  const location = useLocation()
  const { user } = useAuth()
  const normalized = normalizeRoutePath(routePath ?? location.pathname)

  const [articles, setArticles] = useState<KnowledgeEntity[]>(() => helpCache.get(normalized) ?? [])
  const [loading, setLoading] = useState(() => Boolean(user) && !helpCache.has(normalized))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setArticles([])
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    const cached = helpCache.get(normalized)
    if (cached) {
      setArticles(cached)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    fetchHelpForRoute(normalized)
      .then((list) => {
        if (cancelled) return
        setArticles(list)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setArticles([])
        setLoading(false)
        setError(err instanceof Error ? err.message : 'Failed to load help')
      })

    return () => {
      cancelled = true
    }
  }, [normalized, user?.id])

  return {
    articles,
    hasHelp: articles.length > 0,
    loading,
    routePath: normalized,
    error,
  }
}
